// DatabaseDeleteAction.js
// ---------------------------------------------------------------------------
// WHAT: A per-row "Delete permanently" action for the read-only Database
//       Mappings report. On click it PRECHECKS the target (read-only) and then
//       renders the confirmation flow appropriate to the delete-eligibility
//       state:
//         State 1 (never collected)      -> simple confirm, one click to delete.
//         State 2 (collected, no campaign)-> NOT ENABLED yet (this pass): shows
//                                            an informational "not available"
//                                            notice; no delete is offered.
//         State 3 (campaigned/attested)  -> BLOCKED: lists the blocking
//                                            campaigns and offers Deactivate
//                                            (archive) via the existing
//                                            soft-delete endpoint.
//
// WHY: The hard-delete blast radius differs per state; the UI must not offer a
//      one-click destroy on a database that a campaign depends on. Precheck-then-
//      render keeps the destructive path gated behind the backend's own state
//      classification (never the client's guess).
//
// SCOPE: COTS-only feature (see COTS_ONLY_CHANGES.md, Change Set 3). State-2
//        teardown is intentionally deferred to a later pass.
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import axios from 'axios';

import API_CONFIG from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import { WideModal } from './common';

const DatabaseDeleteAction = ({ db, onDeleted }) => {
  const identifier = db.database_identifier;
  const technology = db.technology_label || db.technology;

  // Identity for the permission-gated delete/archive endpoints. Bare axios (used
  // here) does NOT go through the services/api.js interceptor, so we must attach
  // X-Username + the session cookie ourselves — otherwise the backend sees no
  // identity and returns 401 (which rendered as "authentication required" instead
  // of the real state message). Matches DBComplianceMatrix.js's auth pattern.
  const { user } = useAuth();
  const currentUser = user?.username || user?.email || 'unknown_user';
  const authCfg = { headers: { 'X-Username': currentUser }, withCredentials: true };

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);   // precheck in flight
  const [working, setWorking] = useState(false);    // delete/archive in flight
  const [precheck, setPrecheck] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);           // success message
  const [confirmText, setConfirmText] = useState(''); // State-2 typed confirm

  const open = async () => {
    setShow(true);
    setError('');
    setDone(null);
    setPrecheck(null);
    setConfirmText('');
    setLoading(true);
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint(`database-delete/precheck/${encodeURIComponent(identifier)}/`),
        authCfg
      );
      setPrecheck(resp.data?.precheck || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Precheck failed.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (working) return;
    setShow(false);
  };

  // Hard-delete. State 1 needs no confirm; State 2 sends the typed confirm_text.
  const doHardDelete = async () => {
    setWorking(true);
    setError('');
    try {
      await axios.post(
        API_CONFIG.getDatabasesEndpoint(`database-delete/execute/${encodeURIComponent(identifier)}/`),
        { confirm_text: confirmText },
        authCfg
      );
      setDone(`${identifier} deleted.`);
      if (onDeleted) onDeleted(identifier);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed.');
    } finally {
      setWorking(false);
    }
  };

  // State 3: archive (deactivate) via the audited archive endpoint — it sets
  // is_active=false AND writes a governance audit row in one transaction.
  const doArchive = async () => {
    setWorking(true);
    setError('');
    try {
      await axios.post(
        API_CONFIG.getDatabasesEndpoint(`database-delete/archive/${encodeURIComponent(identifier)}/`),
        {},
        authCfg
      );
      setDone(`${identifier} deactivated (archived).`);
      if (onDeleted) onDeleted(identifier);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Archive failed.');
    } finally {
      setWorking(false);
    }
  };

  const state = precheck?.state;

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={close} disabled={working}>
        Close
      </button>
      {!done && state === 1 && (
        <button className="btn btn-danger" onClick={doHardDelete} disabled={working}>
          {working ? 'Deleting…' : 'Delete permanently'}
        </button>
      )}
      {!done && state === 2 && (
        <button
          className="btn btn-danger"
          onClick={doHardDelete}
          disabled={working || confirmText.trim().toUpperCase() !== (precheck?.identifier || '').toUpperCase()}
          title="Type the identifier above to enable"
        >
          {working ? 'Deleting…' : 'Delete permanently'}
        </button>
      )}
      {/* Deactivate: for State 2 always; for State 3 ONLY when no active/unlocked
          campaign blocks it (an in-progress campaign forbids every action). */}
      {!done && (state === 2 ||
                 (state === 3 && !(precheck?.active_unlocked_campaigns || []).length)) && (
        <button className="btn btn-warning" onClick={doArchive} disabled={working}>
          {working ? 'Deactivating…' : 'Deactivate'}
        </button>
      )}
    </>
  );

  return (
    <>
      <button
        className="btn btn-sm btn-outline-danger"
        title="Delete or archive this database"
        onClick={open}
      >
        <i className="fas fa-trash-alt"></i>
      </button>

      {show && (
        <WideModal
          show={show}
          onClose={close}
          title={`Delete database — ${identifier}`}
          headerIcon="fa-trash-alt"
          headerClass="bg-danger text-white"
          maxWidth="900px"
          footer={footer}
        >
          {loading && (
            <div className="text-center py-4">
              <i className="fas fa-spinner fa-spin fa-2x text-muted"></i>
              <div className="mt-2 text-muted">Checking…</div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>{error}
            </div>
          )}

          {done && (
            <div className="alert alert-success">
              <i className="fas fa-check-circle me-2"></i>{done}
            </div>
          )}

          {!loading && !done && precheck && (
            <>
              {/* M:N blast radius: deleting removes the database for EVERY linked
                  application, so name them all before any destructive choice. */}
              {(precheck.linked_applications || []).length > 1 && (
                <div className="alert alert-warning py-2 mb-2">
                  <i className="fas fa-share-nodes me-2"></i>
                  This database serves{' '}
                  <strong>{precheck.linked_applications.length} applications</strong>{' '}
                  ({precheck.linked_applications
                    .map((a) => `${a.is_primary ? '★ ' : ''}${a.app_name || a.app_id}`)
                    .join(', ')}).
                  Deleting or deactivating removes it for all of them.
                </div>
              )}

              {/* ---- State 1: safe permanent delete ---- */}
              {state === 1 && (
                <p className="mb-0">
                  Permanently delete <strong>{identifier}</strong>? It has never
                  been collected, so no access data or campaigns depend on it.
                  <br />
                  <span className="text-muted small">This cannot be undone.</span>
                </p>
              )}

              {/* ---- State 2: collected, no campaign — delete (typed confirm) or archive ---- */}
              {state === 2 && (
                <>
                  <p className="mb-2">
                    <strong>{identifier}</strong> has collected data and
                    references ({precheck.total_rows} rows across{' '}
                    {(precheck.would_purge || []).length} tables — access,
                    categorization, refresh history, and the registration
                    itself). Permanently deleting removes all of it and cannot
                    be undone. Or <strong>Deactivate</strong> to archive it
                    instead (data kept, hidden from reports/campaigns).
                  </p>
                  <label className="form-label small mb-1">
                    To delete permanently, type <code>{identifier}</code>:
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: '320px' }}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={identifier}
                    autoComplete="off"
                    disabled={working}
                  />
                </>
              )}

              {/* ---- State 3: campaigned ---- */}
              {state === 3 && (() => {
                const activeUnlocked = precheck.active_unlocked_campaigns || [];
                const inUse = (precheck.blockers || [])
                  .flatMap((b) => (b.examples && b.examples.length ? b.examples : [b.source]));
                // An active/unlocked campaign (pre-attestation) forbids EVERY action.
                if (activeUnlocked.length > 0) {
                  const shown = activeUnlocked.slice(0, 8);
                  const more = activeUnlocked.length - shown.length;
                  const names = shown.join(', ') + (more > 0 ? `, …and ${more} more` : '');
                  return (
                    <div className="alert alert-danger py-2 mb-0">
                      <i className="fas fa-ban me-2"></i>
                      <strong>{identifier}</strong> is used by active campaign/s
                      ({names}). Deletion or Archiving is not allowed unless the/se
                      campaigns are cancelled or deleted.
                    </div>
                  );
                }
                // All campaigns locked (attestation/completed) → archive is safe.
                return (
                  <p className="mb-0">
                    <strong>{identifier}</strong> is used by a campaign, so it can't
                    be permanently deleted. Choose <strong>Deactivate</strong> below
                    to archive it — its history is kept.
                    {inUse.length > 0 && (
                      <span className="d-block text-muted small mt-2">
                        In use by: {inUse.join(', ')}
                      </span>
                    )}
                  </p>
                );
              })()}
            </>
          )}
        </WideModal>
      )}
    </>
  );
};

export default DatabaseDeleteAction;
